import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  addPowerSystem,
  removePowerSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { PowerSystem } from "../../../../types/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";
import {
  createEmergencyPowerSystemStatsSelector,
  createBatterySystemStatsSelector,
  createSolarPanelStatsSelector,
} from '../../../../store/selectors/derivedSelectors';

interface PowerSystemsProps {
  availableSystems: any[];
  powerPlantTonnage: number;
  powerPlantCost: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

// Solar Panel IDs for dropdown grouping
const SOLAR_PANEL_IDS = [
  "solarPanelsBasic",
  "solarPanelsImproved",
  "solarPanelsEnhanced",
  "solarPanelsAdvanced",
];

export const PowerSystems: React.FC<PowerSystemsProps> = ({
  availableSystems,
  powerPlantTonnage,
  powerPlantCost,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.power);
  // const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive); // This line is removed as per edit hint

  // State for user-selectable systems
  const [batteryTonnage, setBatteryTonnage] = useState<number>(1);
  const [batteryTechLevel, setBatteryTechLevel] = useState<number>(10);
  const [solarPanelTonnage, setSolarPanelTonnage] = useState<number>(1);
  const [selectedSolarPanel, setSelectedSolarPanel] = useState(
    SOLAR_PANEL_IDS[0],
  );

  // Only show canonical Traveller power systems
  const filteredAvailableSystems = availableSystems.filter(
    (system) => system.subtype === "battery" || system.subtype === "emergency",
  );

  // Get solar panel options and selected object
  const solarPanelOptions = availableSystems.filter((s) =>
    SOLAR_PANEL_IDS.includes(s.id),
  );
  const selectedSolarPanelObj = solarPanelOptions.find(
    (s) => s.id === selectedSolarPanel,
  )!;

  // Precompute selector results for special systems
  const emergencyPowerStats = useAppSelector((state) => {
    const selector = createEmergencyPowerSystemStatsSelector(
      powerPlantCost,
      powerPlantTonnage,
    );
    return selector(state);
  });
  // Precompute battery stats for up to two battery systems at the top level, always call hooks
  const batterySystems = filteredAvailableSystems.filter(
    (s) => s.subtype === "battery",
  );
  const batteryStats1 = useAppSelector((state) => {
    const selector = createBatterySystemStatsSelector(
      batteryTonnage,
      batteryTechLevel,
      batterySystems[0] ?? null,
    );
    return selector(state);
  });
  const batteryStats2 = useAppSelector((state) => {
    const selector = createBatterySystemStatsSelector(
      batteryTonnage,
      batteryTechLevel,
      batterySystems[1] ?? null,
    );
    return selector(state);
  });
  const batteryStats: Record<string, any> = {};
  if (batterySystems[0]) batteryStats[batterySystems[0].id] = batteryStats1;
  if (batterySystems[1]) batteryStats[batterySystems[1].id] = batteryStats2;

  // Prepare available systems with dynamic calculations and validation
  const processedAvailableSystems = filteredAvailableSystems.map((system) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    let validationErrors: string[] = [];
    let isInstalled = systems.some((s) => s.id === system.id);

    // Special handling for Emergency Power System
    if (system.subtype === "emergency") {
      calculatedCost = emergencyPowerStats.cost;
      calculatedTonnage = emergencyPowerStats.tonnage;
      calculatedPower = emergencyPowerStats.power;
    } else if (system.subtype === "battery") {
      const stats = batteryStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
    }

    // Tech level validation
    if (!tlOverrideActive && (system.techLevel ?? 0) > techLevel) {
      validationErrors.push(`Requires TL ${system.techLevel}`);
    }

    // Special validation for Emergency Power System
    if (
      system.subtype === "emergency" &&
      (powerPlantTonnage <= 0 || powerPlantCost <= 0)
    ) {
      validationErrors.push("Requires existing power plant");
    }

    return {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      validationErrors,
      isInstalled,
    };
  });

  // Calculate solar panel stats based on selected variant and tonnage using selector
  const solarPanelStats = useAppSelector((state) => {
    const selector = createSolarPanelStatsSelector(
      solarPanelTonnage,
      techLevel,
      selectedSolarPanelObj,
    );
    return selector(state);
  });

  const handleAddSystem = (system: PowerSystem) => {
    let finalSystem = { ...system };

    // Special handling for Emergency Power System
    if (system.subtype === "emergency") {
      finalSystem = {
        ...system,
        cost: emergencyPowerStats.cost,
        tonnage: emergencyPowerStats.tonnage,
        power: emergencyPowerStats.power,
        properties: {
          ...system.properties,
          calculated: true,
          powerPlantCost,
          powerPlantTonnage,
        },
      };
    } else if (system.subtype === "battery") {
      const stats = batteryStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
        properties: {
          ...system.properties,
          selectedTonnage: batteryTonnage,
          selectedTechLevel: batteryTechLevel,
        },
      };
    }

    dispatch(addPowerSystem(finalSystem));
  };

  const handleAddSolarPanel = () => {
    const finalSystem = {
      ...selectedSolarPanelObj,
      cost: solarPanelStats.calculatedCost,
      tonnage: solarPanelStats.calculatedTonnage,
      power: solarPanelStats.calculatedPower,
      properties: {
        ...selectedSolarPanelObj.properties,
        selectedTonnage: solarPanelTonnage,
        selectedTechLevel: techLevel,
      },
    };
    dispatch(addPowerSystem(finalSystem as PowerSystem));
  };

  const handleRemoveSystem = (systemId: string) => {
    dispatch(removePowerSystem(systemId));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Power Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Available Power Systems</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Solar Panels Card with Dropdown */}
          <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Solar Panels
              </div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedSolarPanel}
                onChange={(e) => setSelectedSolarPanel(e.target.value)}
              >
                {solarPanelOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>

              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tonnage
                </label>
                <input
                  type="number"
                  min="1"
                  value={solarPanelTonnage}
                  onChange={(e) => setSolarPanelTonnage(Number(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                />
              </div>

              <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <li>
                  <b>Cost:</b> {formatCurrency(solarPanelStats.calculatedCost)}{" "}
                  Cr
                </li>
                <li>
                  <b>Tonnage:</b> {solarPanelStats.calculatedTonnage.toFixed(1)}{" "}
                  tons
                </li>
                <li>
                  <b>Power:</b> {solarPanelStats.calculatedPower.toFixed(1)}{" "}
                  Power
                </li>
              </ul>
            </div>

            <div className="mt-2 flex gap-2">
              <button
                className="w-full mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={handleAddSolarPanel}
              >
                Add
              </button>
            </div>
          </div>

          {/* Other power systems */}
          {processedAvailableSystems.map((system) => (
            <div
              key={system.id}
              className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {system.name}
                </div>

                {/* Special UI for High-Efficiency Batteries */}
                {system.subtype === "battery" && (
                  <div className="mb-2 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tonnage
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={batteryTonnage}
                        onChange={(e) =>
                          setBatteryTonnage(Number(e.target.value))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                        disabled={system.isInstalled}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tech Level
                      </label>
                      <select
                        value={batteryTechLevel}
                        onChange={(e) =>
                          setBatteryTechLevel(Number(e.target.value))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                        disabled={system.isInstalled}
                      >
                        <option value={10}>TL 10 (40 Power/ton)</option>
                        <option value={12}>TL 12 (60 Power/ton)</option>
                      </select>
                    </div>
                  </div>
                )}

                <ul className="text-xs text-gray-600 mb-2">
                  <li>
                    <b>Cost:</b> {formatCurrency(system.cost)} Cr
                  </li>
                  <li>
                    <b>Tonnage:</b> {system.tonnage.toFixed(1)} tons
                  </li>
                  <li>
                    <b>Power:</b> {system.power.toFixed(1)} Power
                  </li>

                  {/* Special properties for each system type */}
                </ul>

                {system.validationErrors.length > 0 && (
                  <div className="text-xs text-red-600 mb-2">
                    {system.validationErrors.map((err: string, idx: number) => (
                      <div key={idx}>{err}</div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-2 flex gap-2">
                {!system.isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      system.validationErrors.length > 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleAddSystem(system as PowerSystem)}
                    disabled={system.validationErrors.length > 0}
                    title={
                      system.validationErrors.length > 0
                        ? system.validationErrors.join(" ")
                        : ""
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => handleRemoveSystem(system.id)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PowerSystems;
